/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import { Modal } from '../../shared/modals/Modal';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { getPromotionEventTypeLabel } from '../infra/promotionEventQueries';
import type { PromotionEventRow, PromotionEventType } from '../infra/promotionEventQueries';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { OperationalFilterClearLink } from '../../shared/filters/OperationalFilterClearLink';
import { ModalFilterSelectTrigger } from '../../shared/modals/ModalFilterSelectTrigger'
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel';
import { useOperationalFilterLayout } from '../../shared/filters/useOperationalFilterLayout';
import { ticketColors } from '../../shared/theme/ticketColors';

export type PromotionHistoryEventOption = { value: string; label: string };

type Props = {
  visible: boolean;
  promotionName: string;
  events: PromotionEventRow[];
  historyTotalCount?: number;
  historyLoading: boolean;
  historyLoadingMore: boolean;
  historyHasMore: boolean;
  onLoadMore: () => void;
  onClose: () => void;
  actorLabel: (userId: string | null | undefined) => string;
  eventOptions: PromotionHistoryEventOption[];
  eventFilter: PromotionEventType | 'all';
  onEventFilterChange: (value: string) => void;
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onApplyFilters: () => void;
};

export const PromotionHistoryModal = ({
  visible,
  promotionName,
  events,
  historyTotalCount,
  historyLoading,
  historyLoadingMore,
  historyHasMore,
  onLoadMore,
  onClose,
  actorLabel,
  eventOptions,
  eventFilter,
  onEventFilterChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onApplyFilters,
}: Props) => {
  const { variant } = useOperationalFilterLayout();
  const isMobile = variant === 'mobile';
  const insets = useSafeAreaInsets();
  const { height: winH, width: winW } = useWindowDimensions();
  const [eventPickerOpen, setEventPickerOpen] = useState(false);

  const eventLabel = useMemo(() => {
    return eventOptions.find((o) => o.value === eventFilter)?.label ?? 'Todos';
  }, [eventFilter, eventOptions]);

  const hasActiveFilters = eventFilter !== 'all' || fromDate.trim().length > 0 || toDate.trim().length > 0;

  const clearFilters = () => {
    onEventFilterChange('all');
    onFromDateChange('');
    onToDateChange('');
    onApplyFilters();
  };

  const filtersContent = (
    <>
      <Text style={s.sectionTitle}>Filtros</Text>
      <Text style={s.fieldLabel}>Fechas</Text>
      <View style={s.dateRow}>
        <View style={s.dateCol}>
          <Text style={s.dateHint}>Desde</Text>
          <InventoryIsoDateField
            value={fromDate}
            onChange={onFromDateChange}
            emptyLabel="Sin fecha"
            ariaLabel="Fecha desde"
            clearButtonLabel="Quitar"
            iosEmptyBehavior="openPicker"
          />
        </View>
        <View style={s.dateCol}>
          <Text style={s.dateHint}>Hasta</Text>
          <InventoryIsoDateField
            value={toDate}
            onChange={onToDateChange}
            emptyLabel="Sin fecha"
            ariaLabel="Fecha hasta"
            clearButtonLabel="Quitar"
            iosEmptyBehavior="openPicker"
          />
        </View>
      </View>

      <ModalFilterSelectTrigger
        label="Qué pasó"
        value={eventLabel}
        onPress={() => setEventPickerOpen(true)}
        active={hasActiveFilters}
        accessibilityLabel="Elegir qué pasó"
      />

      {hasActiveFilters ? (
        <View style={s.filterClearRow}>
          <OperationalFilterClearLink onPress={clearFilters} />
        </View>
      ) : null}

      <TouchableOpacity style={s.applyBtn} onPress={onApplyFilters}>
        <Text style={s.applyBtnText}>Aplicar filtros</Text>
      </TouchableOpacity>
    </>
  );

  const eventsList = (
    <>
      {historyLoading ? (
        <ModalStatePanel loading loadingMessage="Cargando historial…" />
      ) : events.length === 0 ? (
        <ModalStatePanel
          empty
          emptyTitle="Sin cambios"
          emptyMessage="Con estos filtros no hay cambios."
        />
      ) : (
        <View style={s.listContainer}>
          <FlatList
            data={events}
            keyExtractor={(item) => item.id}
            onEndReached={() => {
              if (historyHasMore && !historyLoadingMore) onLoadMore();
            }}
            onEndReachedThreshold={0.35}
            renderItem={({ item }) => (
              <View style={s.listRow}>
                <View style={s.listRowHeader}>
                  <Text style={s.listRowTitle}>
                    {getPromotionEventTypeLabel(item.eventType)}
                  </Text>
                  <View style={[s.eventBadge, (s[`eventBadge_${item.eventType}` as keyof typeof s] ?? s.eventBadge_default) as typeof s.eventBadge]}>
                    <Text style={s.eventBadgeText}>{item.eventType}</Text>
                  </View>
                </View>
                <Text style={s.listMeta}>{item.summary}</Text>
                <Text style={s.listMetaMuted}>
                  {formatLocalDateTime(item.createdAt)} · {actorLabel(item.performedByUserId)}
                </Text>
              </View>
            )}
            ListFooterComponent={
              <>
                {historyLoadingMore ? (
                  <View style={s.footerLoading}>
                    <ActivityIndicator color={ticketColors.gold} size="small" />
                    <Text style={s.stateText}>Cargando más…</Text>
                  </View>
                ) : null}
                {historyHasMore && !historyLoadingMore ? (
                  <TouchableOpacity style={s.loadMoreBtn} onPress={onLoadMore}>
                    <Text style={s.accentLink}>Cargar más</Text>
                  </TouchableOpacity>
                ) : null}
              </>
            }
            style={s.historyFlatList}
          />
        </View>
      )}
    </>
  );

  const nestedPicker = (
    <ModalNestedPicker
      visible={eventPickerOpen}
      title="Qué pasó"
      options={eventOptions}
      selectedValue={eventFilter}
      onSelect={onEventFilterChange}
      onClose={() => setEventPickerOpen(false)}
    />
  );

  if (isMobile) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
          <View style={s.backdrop}>
            <Pressable
              style={StyleSheet.absoluteFill}
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            />
            <View style={[s.mobileSheet, { paddingBottom: Math.max(insets.bottom, spacing.sm) }]}>
              <View style={s.grabber} />
              <View style={s.mobileHeader}>
                <View style={s.mobileHeaderText}>
                  <Text style={s.mobileTitle}>Historial de promoción</Text>
                  <Text style={s.mobileSubtitle} numberOfLines={1}>{promotionName}</Text>
                </View>
                <TouchableOpacity
                  onPress={onClose}
                  style={s.closeBtn}
                  activeOpacity={0.6}
                  accessibilityRole="button"
                  accessibilityLabel="Cerrar"
                >
                  <Ionicons name="close" size={18} color={ticketColors.ink3} />
                </TouchableOpacity>
              </View>

              <ModalAwareScrollView
                style={s.mobileScroll}
                contentContainerStyle={s.mobileScrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
                bottomOffset={spacing.sm}
              >
                <View style={s.kpiRow}>
                  <View style={s.kpiItem}>
                    <Text style={s.kpiNum}>{String(historyTotalCount ?? events.length)}</Text>
                    <Text style={s.kpiLabel}>Cambios</Text>
                  </View>
                </View>

                <Text style={s.scopeHint} numberOfLines={3}>
                  Registro de esta compu o celular. Incluye lo que hiciste aunque hayas trabajado sin conexión.
                </Text>

                <View style={s.filtersPanel}>
                  {filtersContent}
                </View>

                {eventsList}
              </ModalAwareScrollView>

              {nestedPicker}
            </View>
          </View>
      </Modal>
    );
  }

  const wideMaxWidth = Math.min(winW - 80, 720);
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={[s.wideBackdrop, { padding: Math.max(insets.top, spacing.lg) }]}>
        <View style={[s.wideCard, { maxWidth: wideMaxWidth, maxHeight: winH * 0.9 }]}>
          <View style={s.wideHeader}>
            <View style={s.wideHeaderText}>
              <Text style={s.wideTitle}>Historial de promoción</Text>
              <Text style={s.wideSubtitle} numberOfLines={1}>{promotionName}</Text>
            </View>
            <TouchableOpacity
              onPress={onClose}
              style={s.closeBtn}
              activeOpacity={0.6}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
            >
              <Ionicons name="close" size={18} color={ticketColors.ink3} />
            </TouchableOpacity>
          </View>

          <ModalAwareScrollView
            style={s.wideScroll}
            contentContainerStyle={s.wideScrollContent}
            keyboardShouldPersistTaps="handled"
            bottomOffset={spacing.sm}
          >
            <View style={s.wideKpiRow}>
              <View style={s.wideKpiItem}>
                <Text style={s.wideKpiNum}>{String(historyTotalCount ?? events.length)}</Text>
                <Text style={s.wideKpiLabel}>Cambios</Text>
              </View>
            </View>

            <Text style={s.scopeHint} numberOfLines={3}>
              Registro de esta compu o celular. Incluye lo que hiciste aunque hayas trabajado sin conexión.
            </Text>

            <View style={s.filtersPanel}>
              {filtersContent}
            </View>

            {eventsList}
          </ModalAwareScrollView>

          {nestedPicker}
        </View>
      </View>
    </Modal>
  );
};

const s = StyleSheet.create({
  // ── Shared ──
  backdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginTop: 8,
    marginBottom: 12,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  scopeHint: {
    ...typography.caption,
    fontSize: 13,
    color: ticketColors.ink3,
    lineHeight: 18,
    marginBottom: spacing.sm,
  },
  filtersPanel: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    padding: spacing.md,
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  sectionTitle: {
    ...typography.caption,
    fontSize: 11,
    color: ticketColors.ink3,
    fontWeight: '800',
    letterSpacing: 0.35,
    textTransform: 'uppercase',
  },
  fieldLabel: {
    ...typography.caption,
    fontSize: 13,
    color: ticketColors.ink2,
    fontWeight: '600',
  },
  dateRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'flex-start',
  },
  dateCol: {
    flex: 1,
    minWidth: 0,
    gap: spacing.xs,
  },
  dateHint: {
    ...typography.caption,
    fontSize: 12,
    color: ticketColors.ink3,
  },
  filterClearRow: {
    alignItems: 'flex-end',
  },
  applyBtn: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.xs,
  },
  applyBtnText: {
    fontSize: 15,
    fontWeight: '700',
    color: ticketColors.goldInk,
  },
  // ── KPI ──
  kpiRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  kpiItem: {
    flex: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 4,
  },
  kpiNum: {
    fontSize: 22,
    fontWeight: '800',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
    letterSpacing: -0.3,
  },
  kpiLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  // ── Event list ──
  listContainer: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    overflow: 'hidden',
    minHeight: 120,
  },
  historyFlatList: { flex: 1 },
  listRow: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    gap: 6,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  listRowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  listRowTitle: {
    ...typography.body,
    fontSize: 15,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  eventBadge: {
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  eventBadge_default: { backgroundColor: ticketColors.brandSoft },
  eventBadge_create: { backgroundColor: 'rgba(34,197,94,0.12)' },
  eventBadge_update: { backgroundColor: ticketColors.brandSoft },
  eventBadge_activate: { backgroundColor: 'rgba(34,197,94,0.12)' },
  eventBadge_deactivate: { backgroundColor: ticketColors.warnBg },
  eventBadge_delete: { backgroundColor: ticketColors.bad },
  eventBadge_restore: { backgroundColor: 'rgba(34,197,94,0.12)' },
  eventBadgeText: {
    fontSize: 10,
    fontWeight: '700',
    color: ticketColors.ink2,
    textTransform: 'capitalize',
  },
  listMeta: {
    ...typography.caption,
    fontSize: 13,
    color: ticketColors.ink2,
    lineHeight: 18,
  },
  listMetaMuted: {
    ...typography.caption,
    fontSize: 12,
    color: ticketColors.ink3,
  },
  footerLoading: {
    paddingVertical: spacing.md,
    alignItems: 'center',
    gap: spacing.xs,
  },
  stateText: {
    ...typography.caption,
    color: ticketColors.ink2,
    textAlign: 'center',
  },
  loadMoreBtn: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  accentLink: {
    ...typography.caption,
    color: ticketColors.gold,
    fontWeight: '700',
  },
  // ── Mobile sheet ──
  mobileRoot: { flex: 1 },
  mobileSheet: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    maxHeight: '92%',
    overflow: 'hidden',
    zIndex: 1,
  },
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 12,
  },
  mobileHeaderText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  mobileTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  mobileSubtitle: {
    fontSize: 14,
    fontWeight: '500',
    color: ticketColors.ink3,
  },
  mobileScroll: { flexShrink: 1 },
  mobileScrollContent: {
    paddingHorizontal: 20,
    paddingTop: 4,
    paddingBottom: spacing.lg,
  },
  // ── Wide modal ──
  wideBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
  },
  wideCard: {
    backgroundColor: ticketColors.paper,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    width: '100%',
    overflow: 'hidden',
  },
  wideHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  wideHeaderText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  wideTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: ticketColors.ink,
  },
  wideSubtitle: {
    fontSize: 14,
    fontWeight: '500',
    color: ticketColors.ink3,
  },
  wideScroll: { flex: 1 },
  wideScrollContent: { padding: spacing.lg },
  wideKpiRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  wideKpiItem: {
    flex: 1,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 4,
  },
  wideKpiNum: {
    fontSize: 24,
    fontWeight: '800',
    color: ticketColors.ink,
    fontVariant: ['tabular-nums'],
    letterSpacing: -0.3,
  },
  wideKpiLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: ticketColors.ink3,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
});
